import { createContext, useContext, useEffect, useReducer } from 'react';
import { CART_STORAGE_KEY, cartItemView, cartReducer, readCart } from 'lib/paperworkCart';
import { usePackagePrices } from 'lib/paperworkPrices';

const CartContext = createContext(null);
export default function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, undefined, readCart);
  // This subscription triggers a render when the shared price source changes.
  usePackagePrices({ load: false });
  useEffect(() => {
    try { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ version: 2, ...state })); } catch { /* Cart remains usable in this tab. */ }
  }, [state]);
  useEffect(() => {
    const sync = event => {
      if (event.storageArea && event.storageArea !== localStorage) return;
      if (event.key === CART_STORAGE_KEY || event.key === null) dispatch({ type: 'hydrate', value: readCart() });
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);
  // cartItemView reads that shared source; derive it on every subscribed render.
  const items = state.items.map(cartItemView);
  const value = {
    items,
    itemCount: items.reduce((sum, item) => sum + item.quantity * item.paperworkPackageCount, 0),
    subtotalMinor: items.reduce((sum, item) => sum + item.priceMinor * item.quantity, 0),
    addItem: (packageId, arrangement, equipment, state) => dispatch({ type: 'add', packageId, arrangement, equipment, state }),
    removeItem: key => dispatch({ type: 'remove', key }),
    restoreItem: ({ packageId, arrangement, equipment, state, quantity, customized }, index) =>
      dispatch({ type: 'restore', index, item: { packageId, arrangement, equipment, state, quantity, ...(customized ? { customized: true } : {}) } }),
    customizeItem: (key, customized) => dispatch({ type: 'customize', key, customized }),
    updateQuantity: (key, quantity) => dispatch({ type: 'quantity', key, quantity }),
    clearCart: (orderId, purchasedItems) => dispatch({ type: 'clear', orderId, items: purchasedItems }),
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('CartProvider is required');
  return context;
}
